import api from "@/configs/axios-config";

export type childPartInfo = {
  qty: number;
  material_code: string;
  amount: number;
  ratio: number;
  consumption: number;
  part_weight: number;
  scrap_weight: number;
  material_price: number;
};

export type childPartMaterialCostV1 = {
  material_cost: number;
  is_purchased: boolean;
  scrap_price: number;
  admin: number;
  profit: number;
  second_tier_process_total: number;
  second_tier_process: Array<{
    id: number;
    ms_second_tier_process_v1_id: number;
    cost: number;
  }>;
  second_tier_process_comment: string;
  other_process_total: number;
  other_process: Array<{
    id: number;
    ms_other_process_v1_id: number;
    cost: number;
  }>;
  other_process_comment: string;
  pct: number;
  other_pct: number;
};


export type childPartMaterialCostV2 = {
  material_cost: number;
  is_purchased: boolean;
  scrap_price: number;
  admin: number;
  profit: number;
  second_tier_process_total: number;
  second_tier_process: Array<{
    id: number;
    part_material_second_tier_process_v2_id: number;
    cost: number;
  }>;
  second_tier_process_comment: string;
  other_process_total: number;
  other_process: Array<{
    id: number;
    part_material_other_process_v2_id: number;
    cost: number;
  }>;
  other_process_comment: string;
  percent: number;
  other_percent: number;
};

export type childPartProcessV1 = {
  oem_process_total: number;
  oem_process: Array<{
    id: number;
    ms_oem_process_v1_id: number;
    cost: number;
  }>;
  oem_process_comment: string;
  oem_process_admin: number;
  oem_process_admin_unit_type_id: number;
  oem_process_profit: number;
  oem_process_profit_unit_type_id: number;
  spare_process_total: number;
  spare_process: Array<{
    id: number;
    ms_spare_process_v1_id: number;
    cost: number;
  }>;
  spare_process_comment: string;
  spare_process_admin: number;
  spare_process_admin_unit_type_id: number;
  spare_process_profit: number;
  spare_process_profit_unit_type_id: number;
  oem_price: number;
  oem_admin: number;
  oem_admin_unit_type_id: number;
  oem_profit: number;
  oem_profit_unit_type_id: number;
  spare_price: number;
  spare_admin: number;
  spare_admin_unit_type_id: number;
  spare_profit: number;
  spare_profit_unit_type_id: number;
};

export type childPartProcessV2 = {
  cp_process_total: number;
  cp_process_cost: number;
  cp_process: Array<{
    id: number;
    part_process_v2_name: string;
    part_process_v2_id: number;
    detail: string;
    qty_1: number;
    cost_per_qty_1: number;
    qty_2: number;
    cost_per_qty_2: number;
    tooling_name: string;
    tooling_cost: number;
    total_cost: number;
  }>;
};

export type childPartV1 = {
  id: number;
  child_part_no: string;
  child_part_name: string;
  oem_price: number;
  spare_price: number;
  total_material_cost: number;
  child_part_info: childPartInfo;
  child_part_material_cost: childPartMaterialCostV1;
  child_part_process: childPartProcessV1;
};

export type childPartV2 = {
  id: number;
  child_part_no: string;
  child_part_name: string;
  total_material_cost: number;
  child_part_info: childPartInfo;
  child_part_material_cost: childPartMaterialCostV2;
  child_part_process: childPartProcessV2;
};

export type childPartV2Param = {
  child_part_v2_id?: number;
  no: string;
  name: string;
  qty: number;
  material_id: number;
  amount: number;
  ratio: number;
  volume: number;
  consumption: number;
  part_weight: number;
  material_price: number;
  material_cost: number;
  scrap_price: number;
  return_scrap_price: number;
  is_purchase_part: boolean;
  admin: number;
  profit: number;
  percent: number;
  second_tier_process_comment: string;
  child_part_material_second_tier_processes_v2: Array<{
    part_material_second_tier_process_v2_id: number;
    cost: number;
  }>;
  other_process_comment: string;
  child_part_material_other_processes_v2: Array<{
    part_material_other_process_v2_id: number;
    cost: number;
  }>;
  child_part_processes_v2: Array<{
    part_process_v2_id: number;
    detail: string;
    qty_1: number;
    cost_per_qty_1: number;
    qty_2: number;
    cost_per_qty_2: number;
    tooling_name: string;
    tooling_cost: number;
  }>;
};

export const getChildPartV1 = async (
  partId: number,
): Promise<childPartV1[]> => {
  const response = await api.get(
    `/first-tier-suppliers/child-parts/v1/${partId}`,
  );
  return response.data;
};

export const getChildPartV2 = async (
  partId: number,
): Promise<childPartV2[]> => {
  const response = await api.get(
    `/first-tier-suppliers/child-parts/v2/${partId}`,
  );
  return response.data;
};

export const createChildPartV2 = async (
  partId: number,
  supplierCode: string,
  params: childPartV2Param,
  flagged_reason: Set<string>,
) => {
  const response = await api.post(
    `/first-tier-suppliers/create/v2/child-parts/${supplierCode}/${partId}`,
    { ...params, flagged_reason: Array.from(flagged_reason) },
  );
  return response.data;
};

export const updateChildPartV2 = async (
  partId: number,
  supplierCode: string,
  params: Array<childPartV2Param>,
  flagged_reason: Set<string>,
) => {
  const response = await api.put(
    `/first-tier-suppliers/update/v2/child-parts/${supplierCode}/${partId}`,
    { child_parts: params, flagged_reason: Array.from(flagged_reason) },
  );
  return response.data;
};

export const deleteChildPartV2 = async (
  partId: number,
  childPartIdList: Array<number>,
) => {
  const response = await api.delete(
    `/first-tier-suppliers/delete/v2/child-parts`,
    { data: { part_id: partId, child_part_v2_ids: childPartIdList } },
  );
  return response.data;
};

export type breakdownProcess = {
  id: number;
  name: string;
  master_process_id?: number;
};

export type breakdownProcessV1 = {
  second_tier_process: Array<breakdownProcess>;
  other_process: Array<breakdownProcess>;
  oem_process: Array<breakdownProcess>;
  spare_process: Array<breakdownProcess>;
};

export type breakdownProcessV2 = {
  second_tier_process: Array<breakdownProcess>;
  other_process: Array<breakdownProcess>;
  cp_process: Array<breakdownProcess>;
  oem_process: Array<breakdownProcess>;
  spare_process: Array<breakdownProcess>;
  oem_other_process: Array<breakdownProcess>;
  spare_other_process: Array<breakdownProcess>;
};

type breakdownProcessV2ApiResponse = {
  second_tier_process: Array<{
    id: number;
    name: string;
    ms_material_second_tier_process_id?: number;
  }>;
  other_process: Array<{
    id: number;
    name: string;
    ms_material_other_process_id?: number;
  }>;
  cp_process: Array<{
    id: number;
    name: string;
    ms_child_part_process_id?: number;
  }>;
  oem_process: Array<{
    id: number;
    name: string;
    ms_oem_process_v2_id?: number;
  }>;
  spare_process: Array<{
    id: number;
    name: string;
    ms_spare_process_v2_id?: number;
  }>;
  oem_other_process: Array<{
    id: number;
    name: string;
    ms_oem_other_process_v2_id?: number;
  }>;
  spare_other_process: Array<{
    id: number;
    name: string;
    ms_spare_other_process_v2_id?: number;
  }>;
};

const transformBreakdownProcessV2 = (
  response: breakdownProcessV2ApiResponse,
): breakdownProcessV2 => {
  return {
    second_tier_process: response.second_tier_process.map((item) => ({
      id: item.id,
      name: item.name,
      master_process_id: item.ms_material_second_tier_process_id,
    })),
    other_process: response.other_process.map((item) => ({
      id: item.id,
      name: item.name,
      master_process_id: item.ms_material_other_process_id,
    })),
    cp_process: response.cp_process.map((item) => ({
      id: item.id,
      name: item.name,
      master_process_id: item.ms_child_part_process_id,
    })),
    oem_process: response.oem_process.map((item) => ({
      id: item.id,
      name: item.name,
      master_process_id: item.ms_oem_process_v2_id,
    })),
    spare_process: response.spare_process.map((item) => ({
      id: item.id,
      name: item.name,
      master_process_id: item.ms_spare_process_v2_id,
    })),
    oem_other_process: response.oem_other_process.map((item) => ({
      id: item.id,
      name: item.name,
      master_process_id: item.ms_oem_other_process_v2_id,
    })),
    spare_other_process: response.spare_other_process.map((item) => ({
      id: item.id,
      name: item.name,
      master_process_id: item.ms_spare_other_process_v2_id,
    })),
  };
};

export const getBreakdownProcessV1 = async (
  supplierCode: string,
): Promise<breakdownProcessV1> => {
  const response = await api.get(
    `/first-tier-suppliers/process/v1/${supplierCode}`,
  );
  return response.data;
};

export const getBreakdownProcessV2 = async (
  partId: number,
): Promise<breakdownProcessV2> => {
  const response = await api.get<breakdownProcessV2ApiResponse>(
    `/first-tier-suppliers/process/v2/${partId}`,
  );
  return transformBreakdownProcessV2(response.data);
};

export type materialProcessParams = {
  material_second_tier_processes: Array<{
    part_material_second_tier_process_id?: number;
    ms_material_second_tier_process_id?: number;
  }>;
  material_other_processes: Array<{
    part_material_other_process_id?: number;
    ms_material_other_process_id?: number;
  }>;
};

export const createMaterialProcessV2 = async (
  partId: number,
  params: materialProcessParams,
) => {
  const response = await api.post(
    `/first-tier-suppliers/create/v2/part-material-processes/${partId}`,
    params,
  );
  return response.data;
};

export const updateMaterialProcessV2 = async (
  partId: number,
  params: materialProcessParams,
) => {
  const response = await api.put(
    `/first-tier-suppliers/update/v2/part-material-processes/${partId}`,
    params,
  );
  return response.data;
};

export const deleteMaterialProcessV2 = async (
  partId: number,
  params: materialProcessParams,
) => {
  const response = await api.delete(
    `/first-tier-suppliers/delete/v2/part-material-processes/${partId}`,
    { data: params },
  );
  return response.data;
};

export type childPartProcessParams = {
  cp_processes: Array<{
    part_cp_process_id?: number;
    ms_child_part_process_id?: number;
  }>;
};

export const createChildPartProcessV2 = async (
  partId: number,
  params: childPartProcessParams,
) => {
  const response = await api.post(
    `/first-tier-suppliers/create/v2/part-cp-processes/${partId}`,
    params,
  );
  return response.data;
};

export const updateChildPartProcessV2 = async (
  partId: number,
  params: childPartProcessParams,
) => {
  const response = await api.put(
    `/first-tier-suppliers/update/v2/part-cp-processes/${partId}`,
    params,
  );
  return response.data;
};

export const deleteChildPartProcessV2 = async (
  partId: number,
  params: childPartProcessParams,
) => {
  const response = await api.delete(
    `/first-tier-suppliers/delete/v2/part-cp-processes/${partId}`,
    { data: params },
  );
  return response.data;
};

export type partProcessParams = {
  oem_processes: Array<{
    part_oem_process_id?: number;
    ms_oem_process_v2_id?: number;
  }>;
  spare_processes: Array<{
    part_spare_process_id?: number;
    ms_spare_process_v2_id?: number;
  }>;
};

export const createPartProcessV2 = async (
  partId: number,
  params: partProcessParams,
) => {
  const response = await api.post(
    `/first-tier-suppliers/create/v2/manage-part-processes/${partId}`,
    params,
  );
  return response.data;
};

export const updatePartProcessV2 = async (
  partId: number,
  params: partProcessParams,
) => {
  const response = await api.put(
    `/first-tier-suppliers/update/v2/manage-part-processes/${partId}`,
    params,
  );
  return response.data;
};

export const deletePartProcessV2 = async (
  partId: number,
  params: partProcessParams,
) => {
  const response = await api.delete(
    `/first-tier-suppliers/delete/v2/manage-part-processes/${partId}`,
    { data: params },
  );
  return response.data;
};

export type partOtherProcessParams = {
  oem_other_processes: Array<{
    part_oem_other_process_id?: number;
    ms_oem_other_process_v2_id?: number;
  }>;
  spare_other_processes: Array<{
    part_spare_other_process_id?: number;
    ms_spare_other_process_v2_id?: number;
  }>;
};

export const createPartOtherProcessV2 = async (
  partId: number,
  params: partOtherProcessParams,
) => {
  const response = await api.post(
    `/first-tier-suppliers/create/v2/manage-part-other-processes/${partId}`,
    params,
  );
  return response.data;
};

export const updatePartOtherProcessV2 = async (
  partId: number,
  params: partOtherProcessParams,
) => {
  const response = await api.put(
    `/first-tier-suppliers/update/v2/manage-part-other-processes/${partId}`,
    params,
  );
  return response.data;
};

export const deletePartOtherProcessV2 = async (
  partId: number,
  params: partOtherProcessParams,
) => {
  const response = await api.delete(
    `/first-tier-suppliers/delete/v2/manage-part-other-processes/${partId}`,
    { data: params },
  );
  return response.data;
};

export type partProcess = {
  oem_part_process: Array<partProcessRow>;
  total_oem_part_process: number;
  admin_oem_part_process: number;
  profit_oem_part_process: number;
  spare_part_process: Array<partProcessRow>;
  total_spare_part_process: number;
  admin_spare_part_process: number;
  profit_spare_part_process: number;
  flagged_reason: Array<string>;
};

export type partOtherProcess = {
  oem_other_process: Array<partOtherProcessRow>;
  total_oem_other_part_process: number;
  admin_oem_part_other_process: number;
  oem_profit_part_other_process: number;
  spare_other_process: Array<partOtherProcessRow>;
  total_spare_other_part_process: number;
  admin_spare_part_other_process: number;
  spare_profit_part_other_process: number;
  flagged_reason: Array<string>;
};

export type partProcessRow = {
  id: number;
  ms_process_id: number;
  unit: number;
  price_per_unit: number;
  process_name: string;
  detail: string;
  total_price: number;
};

export type partOtherProcessRow = {
  id: number;
  ms_process_id: number;
  price: number;
  detail: string;
  process_name: string;
};

export const getPartProcessV2 = async (
  partId: number,
): Promise<partProcess> => {
  const response = await api.get(
    `/first-tier-suppliers/part-processes/v2/${partId}`,
  );
  return response.data;
};

export const getPartOtherProcessV2 = async (
  partId: number,
): Promise<partOtherProcess> => {
  const response = await api.get(
    `/first-tier-suppliers/part-other-processes/v2/${partId}`,
  );
  return response.data;
};

export const updatePartProcessV2Detail = async (
  partId: number,
  params: partProcess,
) => {
  const response = await api.put(
    `/first-tier-suppliers/edit/v2/part-process/${partId}`,
    params,
  );
  return response.data;
};

export const updatePartOtherProcessV2Detail = async (
  partId: number,
  params: partOtherProcess,
) => {
  const response = await api.put(
    `/first-tier-suppliers/edit/v2/part-other-process/${partId}`,
    params,
  );
  return response.data;
};

export type partPriceHistory = {
  id: number;
  year: number;
  quarter: number;
  oem_price: number;
  spare_price: number;
  oem_diff: number;
  spare_diff: number;
};

export const getPartPriceHistory = async (
  version: "v1" | "v2",
  partId: number,
): Promise<partPriceHistory[]> => {
  const response = await api.get(
    `/first-tier-suppliers/part-price/history/${version}/${partId}`,
  );
  return response.data;
};
